import { formatDate, plural, titleCase } from '@westhetics/ui'
import type { Channels, DeliveryItem, SignatureFilter, SignatureRow, SignatureSort } from './api'

/**
 * As frases da área de assinaturas (docs/areas/documentos-e-assinaturas.md; docs/styleguide/04-escrita.md):
 * filtros, canais, coluna Envio, avisos com Desfazer e o WhatsApp assistido (P-ASS-21).
 */

export const FILTERS: readonly SignatureFilter[] = ['pending', 'awaiting_patient', 'signed', 'all']

export const FILTER_LABELS: Record<SignatureFilter, string> = {
  pending: 'Pendentes',
  awaiting_patient: 'Aguardando paciente',
  signed: 'Assinados',
  all: 'Todos',
}

export const CHANNEL_LABELS: Record<Channels, string> = {
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  both: 'Os dois',
}

/** "por WhatsApp" · "por e-mail" · "por WhatsApp e e-mail". */
export const CHANNEL_IN_SENTENCE: Record<Channels, string> = {
  whatsapp: 'WhatsApp',
  email: 'e-mail',
  both: 'WhatsApp e e-mail',
}

export const SORT_NOTES: Record<SignatureSort, string> = {
  severity:
    'A ordem é por gravidade e, depois, por dias em aberto. O que já foi feito sem assinatura vem primeiro.',
  '-sentAt': 'Os envios mais recentes primeiro.',
  sentAt: 'Os envios mais antigos primeiro.',
  patient: 'Em ordem alfabética pelo nome da paciente.',
  '-daysOpen': 'Os envios há mais tempo em aberto primeiro.',
  '-totalAmountCents': 'Os maiores valores primeiro.',
}

export const CRITERION =
  'A ordem é por dias em aberto. O que já foi feito sem assinatura vem primeiro.'

/** "60 no período · 6 dependem de você". */
export function subtitle(counts: { all: number; pending: number } | undefined) {
  if (!counts) return 'Enviadas, assinadas e o que ainda depende de você.'
  return `${counts.all} no período · ${counts.pending} ${counts.pending === 1 ? 'depende' : 'dependem'} de você`
}

/** O primeiro nome, em caixa de título. */
export function firstName(name: string) {
  return titleCase(name).split(' ')[0] ?? ''
}

/** "03/08 · 33d" na coluna Envio; gerado sem envio: "gerado 03/08". */
export function sentCell(row: Pick<SignatureRow, 'sentAt' | 'generatedAt' | 'daysOpen'>) {
  if (!row.sentAt) return `gerado ${formatDate(new Date(row.generatedAt), { year: false })}`
  const day = formatDate(new Date(row.sentAt), { year: false })
  return row.daysOpen === null ? day : `${day} · ${row.daysOpen}d`
}

/** "33 dias" na coluna Em aberto; vazio quando não está em aberto. */
export function daysOpenText(daysOpen: number | null) {
  if (daysOpen === null) return ''
  return daysOpen === 0 ? 'hoje' : plural(daysOpen, 'dia')
}

export function documentsWord(count: number) {
  return `${count} documento${count === 1 ? '' : 's'}`
}

/** Toast do envio (RN-ASS-14): "3 documentos a caminho de Amanda". */
export function sentToast(count: number, patientFirstName: string) {
  return `${documentsWord(count)} a caminho de ${patientFirstName}`
}

/** Toast do reenvio: "Documentos reenviados para Amanda por WhatsApp". */
export function resentToast(patientFirstName: string, channels: Channels) {
  return `Documentos reenviados para ${patientFirstName} por ${CHANNEL_IN_SENTENCE[channels]}`
}

/** Toast do cancelamento (RN-ASS-24). */
export function canceledToast(patientFirstName: string) {
  return `Envio de ${patientFirstName} cancelado. Os documentos continuam no histórico como cancelados.`
}

/** Toast do lote (RN-ASS-13): "2 documentos reenviados por WhatsApp. Amanda já assinou: nada a reenviar." */
export function batchToast(
  result: { resent: { patient: string }[]; skipped: { patient: string | null; reason: string }[] },
  channels: Channels,
) {
  const sent = `${result.resent.length === 1 ? '1 envio reenviado' : `${result.resent.length} envios reenviados`} por ${CHANNEL_IN_SENTENCE[channels]}.`
  const skipped = result.skipped
    .slice(0, 2)
    .map((s) => `${s.patient ? firstName(s.patient) : 'Uma assinatura'}: ${s.reason}.`)
    .join(' ')
  return skipped ? `${sent} ${skipped}` : sent
}

/** Por que o canal não pode ser escolhido para esta paciente; `null` quando pode. */
export function channelBlocker(
  channels: Channels,
  contact: { whatsapp: string | null; email: string | null },
) {
  const noWhatsapp = !contact.whatsapp
  const noEmail = !contact.email
  if (channels === 'whatsapp' && noWhatsapp) return 'Sem WhatsApp na ficha.'
  if (channels === 'email' && noEmail) return 'Sem e-mail na ficha.'
  if (channels === 'both' && noWhatsapp && noEmail) return 'Sem WhatsApp nem e-mail na ficha.'
  if (channels === 'both' && noWhatsapp) return 'Falta o WhatsApp na ficha.'
  if (channels === 'both' && noEmail) return 'Falta o e-mail na ficha.'
  return null
}

/** O primeiro canal possível para a paciente, na ordem do desenho (WhatsApp primeiro). */
export function firstOpenChannel(contact: {
  whatsapp: string | null
  email: string | null
}): Channels {
  const order: Channels[] = ['both', 'whatsapp', 'email']
  return order.find((c) => channelBlocker(c, contact) === null) ?? 'whatsapp'
}

/** O WhatsApp da pessoa com a mensagem pronta (P-ASS-21): `https://wa.me/55119…?text=…`. */
export function whatsappHref(phoneE164: string, text: string) {
  return `https://wa.me/${phoneE164.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`
}

/** Copia para a área de transferência; `false` quando o navegador não deixa. */
export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/** "dd/mm/aaaa hh:mm" no fuso do navegador (a clínica está nele). */
export function formatDateTime(iso: string) {
  const d = new Date(iso)
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${formatDate(d)} ${hh}:${mm}`
}

const DELIVERY_WORDS: Record<DeliveryItem['status'], string> = {
  queued: 'na fila',
  sent: 'enviado',
  delivered: 'entregue',
  read: 'lido',
  failed: 'não entregue',
  canceled: 'cancelado',
  not_sent: 'sem endereço',
  manual: 'pela clínica',
}

/** "WhatsApp · lido" para uma entrega, com "aberto"/"devolvido" no e-mail. */
export function deliveryWord(item: DeliveryItem) {
  if (item.channel === 'email' && item.status === 'read') return 'aberto'
  if (item.channel === 'email' && item.status === 'failed') return 'devolvido'
  return DELIVERY_WORDS[item.status]
}

export const TIMELINE_LABELS: Record<string, string> = {
  send_requested: 'Envio pedido',
  resent: 'Link reenviado',
  queued: 'Mensagens na fila',
  sent: 'Mensagem enviada',
  delivered: 'Mensagem entregue',
  read: 'Mensagem lida',
  failed: 'Mensagem não entregue',
  reminder_sent: 'Lembrete enviado',
  link_opened: 'Abriu o link',
  document_read: 'Leu o documento',
  signed: 'Assinou',
  declined: 'Não concordou',
  expired: 'Link venceu',
  canceled: 'Envio cancelado',
  undone: 'Ação desfeita',
}

export const ROLE_LABELS = {
  patient: 'Paciente',
  guardian: 'Responsável legal',
  clinic_signer: 'Responsável pela clínica',
} as const

export const MODE_LABELS = {
  westhetics: 'traço no celular, pelo link',
  manual: 'logada no sistema',
  automatic: 'assinatura automática da clínica',
  external_provider: 'na Autentique',
} as const

/** "Enviado pela Autentique por WhatsApp e e-mail" (RN-ASS-36): quem entrega o link é ela. */
export function providerSentLine(channels: Channels) {
  return `Enviado pela Autentique por ${CHANNEL_IN_SENTENCE[channels]}`
}

/**
 * Quem entrega, no seletor de canal, quando o envio vai pela Autentique (RN-ASS-36, P-ASS-25): ela manda
 * pelo WhatsApp ou pelo e-mail; nos dois, o WhatsApp é dela e o e-mail sai pelo sistema com o mesmo link.
 */
export const PROVIDER_CHANNEL_NOTES: Record<Channels, string> = {
  whatsapp: 'a Autentique manda pelo WhatsApp',
  email: 'a Autentique manda pelo e-mail',
  both: 'a Autentique manda pelo WhatsApp e o e-mail sai com o mesmo link',
}

/** A frase de caminho quando a responsável assina na Autentique, não daqui (RN-ASS-37). */
export function providerClinicSignerLine(legalEmail: string | null | undefined) {
  return legalEmail
    ? `A responsável assina na Autentique, pelo e-mail enviado a ${legalEmail}.`
    : 'A responsável assina na Autentique, pelo e-mail jurídico da clínica.'
}

/** "WhatsApp · lido · E-mail · entregue" a partir da entrega por canal (a API da lista já manda pronto). */
export function deliverySummaryOf(delivery: DeliveryItem[]) {
  return delivery
    .map((d) => `${d.channel === 'whatsapp' ? 'WhatsApp' : 'E-mail'} · ${deliveryWord(d)}`)
    .join(' · ')
}
